import { FC, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import Url from "../../../util/url";
import { ISprint } from "@hero/shared/models/sprint";
import { IUserInfo } from "@hero/shared/models/userInfo";
import Config from "@hero/shared/models/config";
import Button from "@material-ui/core/Button";
import "./SprintReviewPanel.scss";

interface SprintReport {
    _id: string;
    user: any;
    status: string;
    taskSummary: any;
    attendance: Array<{
        date: string;
        checkin: string;
        checkout: string;
        issue: string;
        minutesLate: number;
        excused: boolean;
        excusedReason?: string;
    }>;
    violations: Array<{
        shortId: string;
        title: string;
        type: string;
        count: number;
        dismissed: boolean;
        dismissedReason?: string;
    }>;
    scores: any;
}

const VIOLATION_LABELS: Record<string, string> = {
    stale: "Không cập nhật >48h",
    todo_idle: "TODO Idle",
    overdue: "Quá hạn",
    escalation: "Leo thang",
    wip: "Quá nhiều task IP",
    no_estimate: "Chưa estimate",
    returned: "Bị trả lại",
    deadline_50: "Deadline 50%",
    deadline_66: "Deadline 66%",
    deadline_late_same_day: "Muộn trong ngày",
};

const SprintReviewPanel: FC<{ sprint: ISprint }> = ({ sprint }) => {
    const [reports, setReports] = useState<SprintReport[]>([]);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [selectedReport, setSelectedReport] = useState<SprintReport | null>(null);
    const userInfo: IUserInfo = useSelector((state: any) => state.authState.user);

    const isLeaderOrAdmin = userInfo.role === Config.USER_ROLE_ADMIN
        || userInfo.role === Config.USER_ROLE_LEADER
        || userInfo.role === Config.USER_ROLE_MID_LEADER;

    const fetchReports = () => {
        setLoading(true);
        axios.get(Url(`sprint-user-report/sprint/${sprint._id}`))
            .then((res) => {
                const data = res.data || [];
                setReports(data);
                if (selectedReport) {
                    setSelectedReport(data.find((r: any) => r._id === selectedReport._id) || null);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    const generateReports = () => {
        setLoading(true);
        axios.post(Url("sprint-user-report/generate"), { sprintId: sprint._id, force: true })
            .then(() => fetchReports())
            .catch(() => setLoading(false));
    };

    const handleDismiss = (reportId: string, violationIndex: number) => {
        const reason = prompt("Lý do bỏ qua vi phạm:");
        if (reason === null) return;
        axios.patch(Url("sprint-user-report/dismiss-violation"), {
            reportId, violationIndex, reason, dismissedBy: userInfo._id,
        }).then(() => fetchReports());
    };

    const handleExcuse = (reportId: string, attendanceIndex: number) => {
        const reason = prompt("Lý do bỏ qua (VD: đã báo trước):");
        if (reason === null) return;
        axios.patch(Url("sprint-user-report/excuse-attendance"), {
            reportId, attendanceIndex, reason, excusedBy: userInfo._id,
        }).then(() => fetchReports());
    };

    const handleSendAndEndSprint = () => {
        if (!window.confirm("Gửi báo cáo sprint cho nhân viên và kết thúc sprint?\n\nSau khi gửi:\n- Báo cáo sẽ được khoá (không sửa được)\n- Nhân viên nhận DM báo cáo qua Discord")) return;
        setSending(true);
        axios.post(Url(`sprint-user-report/send-reports/${sprint._id}`))
            .then((res) => {
                return axios.patch(Url(`sprint-user-report/lock-sprint/${sprint._id}`), {
                    lockedBy: userInfo._id,
                }).then(() => res);
            })
            .then((res) => {
                setSent(true);
                alert(`Đã gửi ${res.data.sent} báo cáo và khoá kết quả.`);
                fetchReports();
            })
            .catch(() => alert("Lỗi khi gửi báo cáo"))
            .finally(() => setSending(false));
    };

    useEffect(() => {
        if (sprint._id) fetchReports();
    }, [sprint._id]);

    if (!isLeaderOrAdmin) return null;

    const reportsWithIssues = reports.filter(
        (r) => r.violations.some((v) => !v.dismissed) || r.attendance.some((a) => !a.excused)
    );

    return (
        <div className="sr-panel">
            <div className="sr-header">
                <h3>Review báo cáo sprint</h3>
                <div className="sr-actions">
                    <Button variant="outlined" onClick={generateReports} disabled={loading}>
                        {reports.length > 0 ? "Tạo lại" : "Tạo báo cáo"}
                    </Button>
                    {reports.length > 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSendAndEndSprint}
                            disabled={sending || sent || reports.every(r => r.status === "locked")}
                        >
                            {reports.every(r => r.status === "locked")
                                ? "Đã gửi & khoá"
                                : sending ? "Đang gửi..." : "Gửi báo cáo & Kết thúc sprint"}
                        </Button>
                    )}
                </div>
            </div>

            {loading && <div className="sr-loading">Đang tải...</div>}

            {!loading && reports.length === 0 && (
                <div className="sr-empty">Chưa có báo cáo. Bấm "Tạo báo cáo" để bắt đầu.</div>
            )}

            {!loading && reports.length > 0 && (
                <div className="sr-content">
                    <div className="sr-summary">
                        {reports.length} báo cáo | {reportsWithIssues.length} cần review
                        {reports.some(r => r.status === "locked") &&
                            <span className="sr-locked-badge"> | {reports.filter(r => r.status === "locked").length} đã khoá</span>
                        }
                    </div>

                    {reportsWithIssues.length > 0 && (
                        <div className="sr-user-list">
                            {reportsWithIssues.map((r) => {
                                const u = r.user;
                                const name = u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : "?";
                                const vCount = r.violations.filter(v => !v.dismissed).length;
                                const aCount = r.attendance.filter(a => !a.excused).length;
                                const isSelected = selectedReport?._id === r._id;
                                return (
                                    <div
                                        key={r._id}
                                        className={`sr-user-chip ${isSelected ? "sr-user-chip-active" : ""}`}
                                        onClick={() => setSelectedReport(isSelected ? null : r)}
                                    >
                                        <span className="sr-chip-name">{name}</span>
                                        {vCount > 0 && <span className="sr-chip-badge sr-chip-violation">{vCount} vi phạm</span>}
                                        {aCount > 0 && <span className="sr-chip-badge sr-chip-attendance">{aCount} chấm công</span>}
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {selectedReport && (
                        <ReportDetail
                            report={selectedReport}
                            onDismiss={(i) => handleDismiss(selectedReport._id, i)}
                            onExcuse={(i) => handleExcuse(selectedReport._id, i)}
                        />
                    )}
                </div>
            )}
        </div>
    );
};

const ReportDetail: FC<{
    report: SprintReport;
    onDismiss: (index: number) => void;
    onExcuse: (index: number) => void;
}> = ({ report, onDismiss, onExcuse }) => {
    const activeViolations = report.violations.filter((v) => !v.dismissed);
    const dismissedViolations = report.violations.filter((v) => v.dismissed);
    const activeAttendance = report.attendance.filter((a) => !a.excused);
    const excusedAttendance = report.attendance.filter((a) => a.excused);
    const isLocked = report.status === "locked";
    const name = report.user ? `${report.user.firstName || ""} ${report.user.lastName || ""}`.trim() : "?";

    return (
        <div className="sr-detail">
            <h4>{name}</h4>
            <div className="sr-detail-cols">
                <div className="sr-section">
                    <h5>Vi phạm quy trình ({activeViolations.length})</h5>
                    {activeViolations.length === 0 && <div className="sr-none">Không có</div>}
                    {activeViolations.map((v, i) => {
                        const realIndex = report.violations.indexOf(v);
                        return (
                            <div key={i} className="sr-item">
                                <span className="sr-item-tag">{VIOLATION_LABELS[v.type] || v.type}</span>
                                <span className="sr-item-task">{v.shortId}: {v.title}</span>
                                <span className="sr-item-count">x{v.count}</span>
                                {!isLocked && (
                                    <button className="sr-btn-dismiss" onClick={() => onDismiss(realIndex)}>Bỏ qua</button>
                                )}
                            </div>
                        );
                    })}
                    {dismissedViolations.length > 0 && (
                        <div className="sr-dismissed-list">
                            <small>Đã bỏ qua ({dismissedViolations.length}):</small>
                            {dismissedViolations.map((v, i) => (
                                <div key={i} className="sr-item sr-item-dismissed">
                                    <span className="sr-item-tag">{VIOLATION_LABELS[v.type] || v.type}</span>
                                    <span className="sr-item-task">{v.shortId}</span>
                                    <span className="sr-item-reason">{v.dismissedReason}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="sr-section">
                    <h5>Chấm công ({activeAttendance.length})</h5>
                    {activeAttendance.length === 0 && <div className="sr-none">Không có</div>}
                    {activeAttendance.map((a, i) => {
                        const realIndex = report.attendance.indexOf(a);
                        const dateStr = new Date(a.date).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" });
                        const issueLabel = a.issue === "no_checkin" ? "Thiếu checkin"
                            : a.issue === "no_checkout" ? "Thiếu checkout"
                            : `Muộn ${a.minutesLate} phút`;
                        return (
                            <div key={i} className="sr-item">
                                <span className="sr-item-date">{dateStr}</span>
                                <span className="sr-item-issue">{issueLabel}</span>
                                <span className="sr-item-time">{a.checkin || "?"} - {a.checkout || "?"}</span>
                                {!isLocked && (
                                    <button className="sr-btn-excuse" onClick={() => onExcuse(realIndex)}>Đã báo trước</button>
                                )}
                            </div>
                        );
                    })}
                    {excusedAttendance.length > 0 && (
                        <div className="sr-dismissed-list">
                            <small>Đã bỏ qua ({excusedAttendance.length}):</small>
                            {excusedAttendance.map((a, i) => {
                                const dateStr = new Date(a.date).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" });
                                return (
                                    <div key={i} className="sr-item sr-item-dismissed">
                                        <span className="sr-item-date">{dateStr}</span>
                                        <span className="sr-item-reason">{a.excusedReason}</span>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SprintReviewPanel;
